/**
 * `MockChainLocalClient` —— 浏览器内独立账本（T04 的本地降级实现）。
 *
 * 与 `MockChainRpcClient`（连 `web/server` 共享账本）共同实现 `IChainClient`，
 * 切换点唯一在 `services/container.ts`（CHAIN_MODE==='local'）。
 * 不发起任何网络请求，纯内存账本，保证离线可演示转账 / 水龙头 / 关注 / 存证。
 */

import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';
import {
  FAUCET_MICRO,
  FEE_ANCHOR_MICRO,
  FEE_SOCIAL_MICRO,
  FREE_ANCHOR_QUOTA,
  MIN_PAID_CONTENT_MICRO,
  splitPaidContent,
  type Address,
  type Anchor,
  type AppConfig,
  type BehaviorRewardEvent,
  type Channel,
  type ContentPurchaseRecord,
  type ContentPurchaseReq,
  type DexCandle,
  type DexOrder,
  type DexPool,
  type DexTrade,
  type FeedItem,
  type IChainClient,
  type Micro,
  type TxHash,
  type TxRecord,
  type ContentConfig,
} from '@shared/chain';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';

const logger = createLogger('chain:local');

function txHash(input: string): TxHash {
  return ('0x' + bytesToHex(sha256(input))) as TxHash;
}

/** 本地降级账本（CHAIN_MODE==='local'）。 */
export class MockChainLocalClient implements IChainClient {
  private readonly balances = new Map<Address, bigint>();
  private blockHeight = 0;
  private readonly history = new Map<Address, TxRecord[]>();
  private readonly anchors = new Map<string, Anchor>();
  private readonly following = new Map<Address, Set<Address>>();
  private readonly followerCount = new Map<Address, number>();
  private readonly channels = new Map<string, Channel>();
  private readonly registered = new Set<Address>();
  private readonly faucetClaimed = new Set<Address>();
  private readonly anchorCount = new Map<Address, number>();
  /** `${contentCid}:${buyer}` → 购买凭证（幂等判定用）。 */
  private readonly purchases = new Map<string, ContentPurchaseRecord>();

  private bump(): number {
    this.blockHeight += 1;
    return this.blockHeight;
  }

  private record(addr: Address, rec: TxRecord): TxRecord {
    const list = this.history.get(addr) ?? [];
    list.unshift(rec);
    this.history.set(addr, list);
    return rec;
  }

  // ─────────────── 账户 ───────────────

  async registerAccount(address: Address, _pubkeyB64: string): Promise<{ success: boolean; txHash?: TxHash }> {
    this.registered.add(address);
    const h = this.bump();
    return { success: true, txHash: txHash(`register:${address}:${h}`) };
  }

  async getBalance(address: Address): Promise<Micro> {
    return (this.balances.get(address) ?? 0n).toString();
  }

  async getBlockHeight(): Promise<number> {
    return this.blockHeight;
  }

  // ─────────────── 资产 ───────────────

  async transfer(req: { from: Address; to: Address; amountMicro: Micro; signature: string }): Promise<TxRecord> {
    const amt = BigInt(req.amountMicro || '0');
    const fromBal = this.balances.get(req.from) ?? 0n;
    if (fromBal < amt) throw new MeshLinkError('E_BALANCE_INSUFFICIENT');
    this.balances.set(req.from, fromBal - amt);
    this.balances.set(req.to, (this.balances.get(req.to) ?? 0n) + amt);
    const h = this.bump();
    const rec: TxRecord = {
      txHash: txHash(`transfer:${req.from}:${req.to}:${req.amountMicro}:${h}`),
      type: 'transfer',
      from: req.from,
      to: req.to,
      amountMicro: req.amountMicro,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.from, rec);
    this.record(req.to, rec);
    return rec;
  }

  async faucet(address: Address, amountMicro?: Micro): Promise<{ txHash: TxHash; amountMicro: Micro }> {
    if (this.faucetClaimed.has(address)) throw new MeshLinkError('E_FAUCET_USED');
    const amt = amountMicro ?? (FAUCET_MICRO.toString() as Micro);
    this.balances.set(address, (this.balances.get(address) ?? 0n) + BigInt(amt));
    this.faucetClaimed.add(address);
    const h = this.bump();
    return { txHash: txHash(`faucet:${address}:${h}`), amountMicro: amt };
  }

  async getTransactionHistory(address: Address, limit = 50): Promise<TxRecord[]> {
    return (this.history.get(address) ?? []).slice(0, limit);
  }

  // ─────────────── 内容存证 ───────────────

  async anchor(req: {
    author: Address;
    cid: string;
    signature: string;
    kind?: Anchor['kind'];
  }): Promise<{ txHash: TxHash; blockHeight: number; feeMicro: Micro }> {
    const used = this.anchorCount.get(req.author) ?? 0;
    const subsidized = used < FREE_ANCHOR_QUOTA;
    const fee = subsidized ? 0n : FEE_ANCHOR_MICRO;
    const bal = this.balances.get(req.author) ?? 0n;
    if (bal < fee) throw new MeshLinkError('E_BALANCE_INSUFFICIENT');
    if (fee > 0n) this.balances.set(req.author, bal - fee);
    const h = this.bump();
    this.anchorCount.set(req.author, used + 1);
    const anchor: Anchor = {
      cid: req.cid,
      author: req.author,
      blockHeight: h,
      timestamp: Date.now(),
      txHash: txHash(`anchor:${req.cid}:${h}`),
      kind: req.kind ?? 'post',
    };
    this.anchors.set(req.cid, anchor);
    logger.info('本地存证完成', { cid: req.cid.slice(0, 12), subsidized });
    return { txHash: anchor.txHash, blockHeight: h, feeMicro: fee.toString() as Micro };
  }

  async getAnchor(cid: string): Promise<Anchor | null> {
    return this.anchors.get(cid) ?? null;
  }

  // ─────────────── 创作者付费内容 ───────────────

  async purchaseContent(req: ContentPurchaseReq): Promise<ContentPurchaseRecord> {
    const cid = String(req.contentCid ?? '').trim();
    if (!cid) throw new MeshLinkError('E_INVALID_INPUT', 'contentCid 不能为空');
    if (req.buyer === req.seller) throw new MeshLinkError('E_SELF_PURCHASE');

    const key = `${cid}:${req.buyer}`;
    const existing = this.purchases.get(key);
    if (existing) return existing;

    const amount = BigInt(/^\d+$/.test(String(req.amountMicro)) ? String(req.amountMicro) : '0');
    if (amount < MIN_PAID_CONTENT_MICRO) throw new MeshLinkError('E_INVALID_INPUT', '价格低于最低限');
    const bal = this.balances.get(req.buyer) ?? 0n;
    if (bal < amount) throw new MeshLinkError('E_BALANCE_INSUFFICIENT');

    const { creatorMicro, platformMicro } = splitPaidContent(amount);
    this.balances.set(req.buyer, bal - amount);
    this.balances.set(req.seller, (this.balances.get(req.seller) ?? 0n) + creatorMicro);

    const h = this.bump();
    const rec: ContentPurchaseRecord = {
      txHash: txHash(`purchase:${cid}:${req.buyer}:${h}`),
      buyer: req.buyer,
      seller: req.seller,
      contentCid: cid,
      amountMicro: amount.toString(),
      creatorMicro: creatorMicro.toString(),
      platformMicro: platformMicro.toString(),
      blockHeight: h,
      timestamp: Date.now(),
    };
    this.purchases.set(key, rec);
    this.record(req.buyer, {
      txHash: rec.txHash,
      type: 'content_purchase',
      from: req.buyer,
      to: req.seller,
      amountMicro: rec.amountMicro,
      feeMicro: '0',
      blockHeight: h,
      timestamp: rec.timestamp,
      status: 'confirmed',
      memo: cid,
    });
    return rec;
  }

  async hasPurchased(contentCid: string, buyer: Address): Promise<ContentPurchaseRecord | null> {
    const cid = String(contentCid ?? '').trim();
    if (!cid) return null;
    return this.purchases.get(`${cid}:${buyer}`) ?? null;
  }

  // ─────────────── 社交图谱 ───────────────

  async follow(req: {
    action: 'follow' | 'unfollow';
    follower: Address;
    followee: Address;
    signature: string;
  }): Promise<{ txHash: TxHash }> {
    const set = this.following.get(req.follower) ?? new Set<Address>();
    const h = this.bump();
    if (req.action === 'follow') {
      set.add(req.followee);
      this.followerCount.set(req.followee, (this.followerCount.get(req.followee) ?? 0) + 1);
    } else {
      set.delete(req.followee);
      this.followerCount.set(req.followee, Math.max(0, (this.followerCount.get(req.followee) ?? 1) - 1));
    }
    this.following.set(req.follower, set);
    const rec: TxRecord = {
      txHash: txHash(`social:${req.action}:${req.follower}:${req.followee}:${h}`),
      type: 'social',
      from: req.follower,
      to: req.followee,
      feeMicro: FEE_SOCIAL_MICRO.toString() as Micro,
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.follower, rec);
    return { txHash: rec.txHash };
  }

  async unfollow(req: {
    action: 'follow' | 'unfollow';
    follower: Address;
    followee: Address;
    signature: string;
  }): Promise<{ txHash: TxHash }> {
    return this.follow(req);
  }

  async getFollowing(follower: Address): Promise<Address[]> {
    return [...(this.following.get(follower) ?? [])];
  }

  async getFollowerCount(followee: Address): Promise<number> {
    return this.followerCount.get(followee) ?? 0;
  }

  // ─────────────── 全行为挖矿：行为上链（本地模拟） ───────────────

  async socialAct(req: {
    action: 'follow' | 'unfollow' | 'like' | 'collect' | 'subscribe' | 'share';
    from: Address;
    target: string;
    signature: string;
  }): Promise<{ txHash: TxHash }> {
    const h = this.bump();
    if (req.action === 'follow' || req.action === 'unfollow') {
      const set = this.following.get(req.from) ?? new Set<Address>();
      if (req.action === 'follow') {
        set.add(req.target as Address);
        this.followerCount.set(req.target as Address, (this.followerCount.get(req.target as Address) ?? 0) + 1);
      } else {
        set.delete(req.target as Address);
        this.followerCount.set(req.target as Address, Math.max(0, (this.followerCount.get(req.target as Address) ?? 1) - 1));
      }
      this.following.set(req.from, set);
    }
    const rec: TxRecord = {
      txHash: txHash(`social:${req.action}:${req.from}:${req.target}:${h}`),
      type: 'social',
      from: req.from,
      to: req.target,
      feeMicro: FEE_SOCIAL_MICRO.toString() as Micro,
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.from, rec);
    return { txHash: rec.txHash };
  }

  async browse(req: {
    from: Address;
    cid: string;
    duration: number;
    ratio: number;
    signature?: string;
  }): Promise<{ txHash: TxHash; blockHeight?: number }> {
    const h = this.bump();
    const rec: TxRecord = {
      txHash: txHash(`browse:${req.from}:${req.cid}:${h}`),
      type: 'browse',
      from: req.from,
      to: req.cid,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.from, rec);
    return { txHash: rec.txHash, blockHeight: h };
  }

  async search(req: {
    from: Address;
    termHash: string;
    signature?: string;
  }): Promise<{ txHash: TxHash; blockHeight?: number }> {
    const h = this.bump();
    const rec: TxRecord = {
      txHash: txHash(`search:${req.from}:${req.termHash}:${h}`),
      type: 'search',
      from: req.from,
      to: req.termHash,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.from, rec);
    return { txHash: rec.txHash, blockHeight: h };
  }

  async checkin(req: {
    from: Address;
    ts?: number;
    signature?: string;
  }): Promise<{ txHash: TxHash; blockHeight?: number }> {
    const h = this.bump();
    const rec: TxRecord = {
      txHash: txHash(`checkin:${req.from}:${h}`),
      type: 'checkin',
      from: req.from,
      to: '',
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.from, rec);
    return { txHash: rec.txHash, blockHeight: h };
  }

  async referral(req: {
    from: Address;
    referrer: Address;
    signature: string;
  }): Promise<{ txHash: TxHash }> {
    const h = this.bump();
    const rec: TxRecord = {
      txHash: txHash(`referral:${req.from}:${req.referrer}:${h}`),
      type: 'referral',
      from: req.from,
      to: req.referrer,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.from, rec);
    return { txHash: rec.txHash };
  }

  async relayStake(req: {
    action: 'stake' | 'unstake' | 'slash';
    address: Address;
    amountMicro?: string;
    peerId?: string;
    reason?: string;
    signature?: string;
  }): Promise<{ txHash: TxHash }> {
    const h = this.bump();
    const rec: TxRecord = {
      txHash: txHash(`relayStake:${req.address}:${req.action}:${h}`),
      type: 'relay_stake',
      from: req.address,
      to: req.address,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.address, rec);
    return { txHash: rec.txHash };
  }

  async getContentConfig(): Promise<ContentConfig> {
    // 本地降级：与默认线上一致（管理后台仅在 MeshLink 后端生效）
    return {
      maxImageBytes: 4 * 1024 * 1024,
      maxVideoBytes: 4 * 1024 * 1024,
      maxAudioBytes: 4 * 1024 * 1024,
      maxOtherBytes: 4 * 1024 * 1024,
    };
  }

  async getAppConfig(): Promise<AppConfig> {
    // 本地降级：全模块开启（与默认线上一致）
    return {
      modules: { chat: true, feed: true, trade: true, rewards: true, discover: true, search: true, checkin: true, recommend: true, onboarding: true, merchant: true, tasks: true },
      content: await this.getContentConfig(),
    };
  }

  // ─────────────── 支付通道 ───────────────

  async openChannel(req: {
    payer: Address;
    relay: string;
    depositMicro: Micro;
    unitPriceMicroPerMB: number;
    maxBytes: number;
    signature: string;
  }): Promise<{ channelId: string; txHash: TxHash }> {
    const id = `ch_${req.payer.slice(2, 10)}_${this.blockHeight}`;
    const h = this.bump();
    const bal = this.balances.get(req.payer) ?? 0n;
    const dep = BigInt(req.depositMicro || '0');
    if (bal < dep) throw new MeshLinkError('E_CHANNEL_INSUFFICIENT');
    this.balances.set(req.payer, bal - dep);
    const channel: Channel = {
      channelId: id,
      payer: req.payer,
      relay: req.relay,
      balanceMicro: req.depositMicro,
      spentMicro: '0',
      totalBytes: 0,
      unitPriceMicroPerMB: req.unitPriceMicroPerMB,
      maxBytes: req.maxBytes,
      seq: 0,
      status: 'open',
      openedAt: Date.now(),
    };
    this.channels.set(id, channel);
    const rec: TxRecord = {
      txHash: txHash(`channel_open:${id}:${h}`),
      type: 'channel_open',
      from: req.payer,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(req.payer, rec);
    return { channelId: id, txHash: rec.txHash };
  }

  async reportMetering(req: {
    channelId: string;
    bytes: number;
    seq: number;
    signature: string;
  }): Promise<{ accepted: boolean; remainingMicro: Micro }> {
    const ch = this.channels.get(req.channelId);
    if (!ch || ch.status !== 'open') return { accepted: false, remainingMicro: '0' };
    const spent = BigInt(ch.spentMicro || '0') + BigInt(req.bytes);
    const remaining = BigInt(ch.balanceMicro || '0') - spent;
    if (remaining < 0n) return { accepted: false, remainingMicro: ch.balanceMicro };
    ch.spentMicro = spent.toString();
    ch.totalBytes += req.bytes;
    ch.seq = req.seq;
    this.channels.set(req.channelId, ch);
    return { accepted: true, remainingMicro: remaining.toString() as Micro };
  }

  async closeChannel(req: { channelId: string; signature: string }): Promise<{ txHash: TxHash; refundedMicro: Micro }> {
    const ch = this.channels.get(req.channelId);
    if (!ch) throw new MeshLinkError('E_INVALID_INPUT', '通道不存在');
    const spent = BigInt(ch.spentMicro || '0');
    const refund = BigInt(ch.balanceMicro || '0') - spent;
    const h = this.bump();
    ch.status = 'closed';
    ch.closedAt = Date.now();
    this.channels.set(req.channelId, ch);
    if (refund > 0n) this.balances.set(ch.payer, (this.balances.get(ch.payer) ?? 0n) + refund);
    const rec: TxRecord = {
      txHash: txHash(`channel_close:${req.channelId}:${h}`),
      type: 'channel_close',
      from: ch.payer,
      feeMicro: '0',
      blockHeight: h,
      timestamp: Date.now(),
      status: 'confirmed',
    };
    this.record(ch.payer, rec);
    return { txHash: rec.txHash, refundedMicro: refund.toString() as Micro };
  }

  async getChannel(channelId: string): Promise<Channel | null> {
    return this.channels.get(channelId) ?? null;
  }

  // ─────────────── DEX / 生态展示（本地降级：无真链能力） ───────────────

  async dexMarkets(): Promise<DexPool[]> {
    return [];
  }

  async dexCandles(): Promise<DexCandle[]> {
    return [];
  }

  async dexOrders(): Promise<DexOrder[]> {
    return [];
  }

  async dexTrades(): Promise<DexTrade[]> {
    return [];
  }

  async dexSubmit(): Promise<{ id?: string }> {
    throw new MeshLinkError('E_NOT_IMPLEMENTED', '本地模式不支持 DEX 交易，请连接共享链（默认模式）');
  }

  async meshStats(): Promise<Record<string, unknown>> {
    return {};
  }

  async myRewards(): Promise<BehaviorRewardEvent[]> {
    return [];
  }

  async recommendedFeed(): Promise<FeedItem[]> {
    return [];
  }

  async promoCreate(): Promise<{ txHash: TxHash; promoId: string }> {
    throw new Error('本地模拟客户端不支持 promoCreate（请连接真链）');
  }

  async promoRedeem(): Promise<{ txHash: TxHash; splits: Record<string, string> }> {
    throw new Error('本地模拟客户端不支持 promoRedeem（请连接真链）');
  }

  async promoList(): Promise<Record<string, unknown>> {
    return { items: [], redemptions: [] };
  }

  async listAnchors(): Promise<Array<Record<string, unknown>>> {
    return [];
  }
}

export default MockChainLocalClient;
